import { useEffect, useRef } from "react";

import { formatJstTime } from "../features/countdown";

interface FooterBarProps {
  readonly announcements: readonly string[];
  readonly nowEpochMs: number;
  readonly onOpenSettings: () => void;
}

const MIN_PIXELS_PER_SECOND = 24;

export function FooterBar({
  announcements,
  nowEpochMs,
  onOpenSettings,
}: FooterBarProps) {
  const announcementText = announcements.join("　　◆　　");
  const durationSeconds = Math.max(24, announcementText.length * 0.52);
  const trackRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLSpanElement>(null);
  const lastFrameRef = useRef<number | null>(null);
  const offsetRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!announcementText) return;

    const track = trackRef.current;
    const copy = copyRef.current;
    if (!track || !copy) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reducedMotion.matches) {
      track.style.transform = "translate3d(0, 0, 0)";
      return;
    }

    const width = () => copy.offsetWidth;
    let copyWidth = width();

    const tick = (frameMs: number): void => {
      if (copyWidth <= 0) {
        copyWidth = width();
        if (copyWidth <= 0) {
          rafRef.current = window.requestAnimationFrame(tick);
          return;
        }
      }

      if (lastFrameRef.current === null) {
        lastFrameRef.current = frameMs;
      }

      const deltaMs = frameMs - lastFrameRef.current;
      lastFrameRef.current = frameMs;

      const pxPerSecond = Math.max(MIN_PIXELS_PER_SECOND, copyWidth / durationSeconds);
      offsetRef.current = (offsetRef.current + deltaMs * 0.001 * pxPerSecond) % copyWidth;
      track.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
      rafRef.current = window.requestAnimationFrame(tick);
    };

    const observer = new ResizeObserver(() => {
      copyWidth = width();
      if (copyWidth > 0 && offsetRef.current > copyWidth) {
        offsetRef.current %= copyWidth;
      }
    });
    observer.observe(copy);

    rafRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      observer.disconnect();
      track.style.transform = "translate3d(0, 0, 0)";
      lastFrameRef.current = null;
      offsetRef.current = 0;
    };
  }, [announcementText, durationSeconds]);

  return (
    <footer className="footer-bar">
      <div
        className={`ticker ${announcementText ? "" : "ticker--empty"}`.trim()}
        aria-label={announcementText ? `お知らせ: ${announcementText}` : "お知らせ"}
      >
        {announcementText ? (
          <div
            className="ticker-track"
            ref={trackRef}
            aria-hidden="true"
          >
            <span className="ticker-copy" ref={copyRef}>{announcementText}<i>◆</i></span>
            <span className="ticker-copy">{announcementText}<i>◆</i></span>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="clock-button"
        onClick={onOpenSettings}
        aria-label={`現在時刻 ${formatJstTime(nowEpochMs)}。クリックして設定を開く`}
        data-testid="clock-button"
      >
        <span className="clock-meta">
          現在時刻 <small>JST</small>
        </span>
        <time dateTime={new Date(nowEpochMs).toISOString()}>{formatJstTime(nowEpochMs)}</time>
      </button>
    </footer>
  );
}
